// ============================================================================
// dataTypes/liveStreamManagment/audioPipeline/AudioMixerPipeline.ts
// Pipeline audio unifié.
//
// REFONTE v3 — Simplifié et centralisé.
//
// v3.1 — outputGain par entrée.
//
// v3.2 — registryOutputs :
//   Map pour tracker les nœuds déjà connectés via connectRegistryOutput.
//   Empêche les connexions multiples d'un même nœud au busDest.
//
// v3.3 — monitorOutputs :
//   Symétrique à registryOutputs mais pour ctx.destination (haut-parleurs).
//   connectMonitorOutput / disconnectMonitorOutput permettent de contrôler
//   explicitement quelle source est audible localement — une seule à la fois,
//   exactement comme pour le WHIP via connectRegistryOutput.
// ============================================================================

import {
  AudioLevel,
  AudioInput,
  AudioInputConfig,
  AudioEffect,
  AudioEffectFactory,
  AudioMixerEvent,
  AudioMixerEventListener,
  SILENT_LEVEL,
} from './types';

import type { AudioSourceRegistry } from './AudioSourceRegistry';
import { createWhiteNoiseBuffer, FILLER_AUDIO_GAIN } from '../utils_audio';
import { DIAGNOSTIC_PROBES_ENABLED } from '../../../backendServices/debugManagment/remoteLogger';

// ─── Constantes analyseur ─────────────────────────────────────────────────────

const FFT_SIZE     = 256;
const SMOOTHING    = 0.15;
const MIN_DECIBELS = -70;
const MAX_DECIBELS = -10;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function peakLevel(data: Uint8Array): number {
  let peak = 0;
  for (let i = 0; i < data.length; i++) {
    if (data[i] > peak) peak = data[i];
  }
  return (peak / 255) * 100;
}

function createAnalyser(ctx: AudioContext): AnalyserNode {
  const a = ctx.createAnalyser();
  a.fftSize               = FFT_SIZE;
  a.smoothingTimeConstant = SMOOTHING;
  a.minDecibels           = MIN_DECIBELS;
  a.maxDecibels           = MAX_DECIBELS;
  return a;
}

// ─── Classe principale ───────────────────────────────────────────────────────

export class AudioMixerPipeline {

  private ctx:       AudioContext | null = null;
  private busDest:   MediaStreamAudioDestinationNode | null = null;
  private inputs     = new Map<string, AudioInput>();
  private listeners  = new Set<AudioMixerEventListener>();
  private meterRafId: number | null = null;
  private meterCallbacks = new Map<string, (level: AudioLevel) => void>();
  private registryOutputs = new Map<string, AudioNode>(); // ← v3.2
  private monitorOutputs  = new Map<string, AudioNode>(); // ← v3.3
  private clockProbeTimer: ReturnType<typeof setInterval> | null = null; // ← cf. startClockProbe()
  private noiseFloorSrc:  AudioBufferSourceNode | null = null; // ← plancher de bruit du bus, cf. startNoiseFloor()
  private noiseFloorGain: GainNode | null = null;

  // Sortie haut-parleurs des entrées directes du mixeur (sons, jingles…).
  // Un seul gain, piloté par le mute du final de la régie : il coupe les
  // enceintes et JAMAIS le bus WHIP (busDest). Le drapeau vit hors du
  // AudioContext pour survivre à un init() après destroy().
  private localMonitorGain: GainNode | null = null;
  private localMonitorMuted = false;

  private effectFactories = new Map<string, AudioEffectFactory>();
  private sourceRegistry: AudioSourceRegistry | null = null;

  // ── Initialisation / destruction ──────────────────────────────────────────

  init(): void {
    if (this.ctx && this.ctx.state !== 'closed') return;
    this.ctx     = new AudioContext();
    this.busDest = this.ctx.createMediaStreamDestination();
    this.localMonitorGain = this.ctx.createGain();
    this.localMonitorGain.gain.value = this.localMonitorMuted ? 0 : 1;
    this.localMonitorGain.connect(this.ctx.destination);
    this.startNoiseFloor();
    this.startClockProbe();
    console.log('[AudioMixerPipeline] init() — AudioContext créé, busDest créé, plancher de bruit branché');
  }

  /**
   * Plancher de bruit PERMANENT sur le bus de sortie (busDest) — inaudible.
   *
   * POURQUOI. busDest sans aucune entrée branchée ne produit pas « rien » : il
   * produit du SILENCE NUMÉRIQUE, ce qui n'est pas la même chose vu de WebRTC.
   * C'est l'état normal de la régie dès que la source active n'apporte pas
   * d'audio — typiquement une caméra virtuelle OBS, qui est un périphérique
   * vidéo seul : getUserMedia ne rend aucune piste micro, initAudioMixer ne
   * branche donc aucune entrée, et la piste du bus reste 'live' tout en
   * n'émettant aucun paquet.
   *
   * Conséquence en aval, constatée le 2026-08-23 sur Nasa TV : MediaMTX
   * n'enregistre AUCUNE piste audio sur `webrtc_UUID`, le microservice ffmpeg
   * tire une source vidéo seule, et son second étage (`ffmpeg_final`) — qui
   * fige son mapping de streams au premier probe et n'est pas censé redémarrer —
   * meurt dès que la piste audio réapparaît à la bascule suivante (changement de
   * PMT sur le bus). Trois coupures d'antenne en 25 minutes.
   *
   * Le bruit garantit un flux de paquets continu quoi qu'il arrive. Le gain est
   * celui utilisé partout ailleurs dans le pipeline pour garder les graphes
   * WebAudio actifs dans Chrome (cf. AudioSourceRegistry.monitorGainNode) :
   * 0.0001 ≈ -80 dBFS, inaudible, et en dessous du plancher des VU-mètres
   * (MIN_DECIBELS = -70).
   *
   * ⚠️ Branché sur busDest UNIQUEMENT, jamais sur ctx.destination : le bus est
   * ce qui part en WHIP, les haut-parleurs n'ont rien à y gagner.
   *
   * ⚠️ Bruit et NON silence : le silence introduit un décalage son/image sur le
   * programme qui suit, ce que le bruit à très faible gain ne fait pas — même
   * parti pris que la mire du microservice ffmpeg (`anoisesrc amplitude=0.0001`).
   */
  private startNoiseFloor(): void {
    if (!this.ctx || !this.busDest) return;

    const src  = this.ctx.createBufferSource();
    src.buffer = createWhiteNoiseBuffer(this.ctx);
    src.loop   = true;

    const gain = this.ctx.createGain();
    gain.gain.value = FILLER_AUDIO_GAIN;

    src.connect(gain);
    gain.connect(this.busDest);
    src.start();

    this.noiseFloorSrc  = src;
    this.noiseFloorGain = gain;
  }

  /**
   * 🔍 DIAGNOSTIC (posé le 2026-10-20, à retirer une fois l'enquête close —
   * cf. mémoire regie-desync-son-image-on-air).
   *
   * En on air avec une vidéo en source, le son sort de la régie de plus en plus
   * en retard sur l'image (+300 → +460 ms en une minute). Hypothèse : l'horloge
   * de cet AudioContext (celle de la carte son) n'avance pas au même rythme que
   * l'horloge système qui cadence la lecture vidéo et WebRTC — l'écart
   * s'accumule dans le tampon d'entrée du graphe. Toutes les 30 s, compare les
   * deux horloges depuis l'init. En console.warn pour atterrir dans remoteLog.
   */
  private startClockProbe(): void {
    if (!DIAGNOSTIC_PROBES_ENABLED) return;
    if (this.clockProbeTimer) clearInterval(this.clockProbeTimer);
    const ctx = this.ctx;
    if (!ctx) return;
    let t0: number | null = null;
    let p0 = 0;
    this.clockProbeTimer = setInterval(() => {
      if (ctx.state !== 'running') { t0 = null; return; }
      const now = performance.now();
      if (t0 === null) { t0 = ctx.currentTime; p0 = now; return; }
      const audio = ctx.currentTime - t0;
      const wall  = (now - p0) / 1000;
      if (wall < 1) return;
      const driftMsPerMin = (audio - wall) / wall * 60_000;
      console.warn(
        `[AUDIO-CLOCK] ${ctx.sampleRate} Hz | depuis ${wall.toFixed(0)} s : audio/système = ${(audio / wall).toFixed(5)}`
        + ` (dérive ${driftMsPerMin >= 0 ? '+' : ''}${driftMsPerMin.toFixed(0)} ms/min)`
        + ` | baseLatency=${(ctx.baseLatency * 1000).toFixed(0)} ms outputLatency=${((ctx as any).outputLatency * 1000 || 0).toFixed(0)} ms`,
      );
    }, 30_000);
  }

  setSourceRegistry(registry: AudioSourceRegistry): void {
    this.sourceRegistry = registry;
    if (this.ctx) {
      registry.setContext(this.ctx);
    }
    console.log('[AudioMixerPipeline] setSourceRegistry() — registry branché');
  }

  getSourceRegistry(): AudioSourceRegistry | null {
    return this.sourceRegistry;
  }

  getContext(): AudioContext {
    if (!this.ctx || this.ctx.state === 'closed') {
      this.init();
    }
    return this.ctx!;
  }

  getOutputStream(): MediaStream | null {
    return this.busDest?.stream ?? null;
  }

  getDestination(): MediaStreamAudioDestinationNode | null {
    return this.busDest;
  }

  async resume(): Promise<void> {
    if (this.ctx?.state === 'suspended') {
      await this.ctx.resume();
      console.log('[AudioMixerPipeline] resume() — AudioContext repris');
    }
  }

  destroy(): void {
    console.log('[AudioMixerPipeline] destroy() — nettoyage complet');
    if (this.clockProbeTimer) { clearInterval(this.clockProbeTimer); this.clockProbeTimer = null; }
    this.stopMeterLoop();
    this.inputs.forEach((_, id) => this.disconnectInput(id));
    this.inputs.clear();
    this.listeners.clear();
    this.meterCallbacks.clear();
    this.registryOutputs.clear(); // ← v3.2
    this.monitorOutputs.clear();  // ← v3.3
    if (this.noiseFloorSrc) {
      try { this.noiseFloorSrc.stop(); } catch { /* déjà arrêté */ }
      this.noiseFloorSrc.disconnect();
      this.noiseFloorSrc = null;
    }
    if (this.noiseFloorGain) {
      this.noiseFloorGain.disconnect();
      this.noiseFloorGain = null;
    }
    if (this.localMonitorGain) {
      this.localMonitorGain.disconnect();
      this.localMonitorGain = null;
    }
    if (this.ctx && this.ctx.state !== 'closed') {
      this.ctx.close().catch(() => {});
    }
    this.ctx     = null;
    this.busDest = null;
  }

  // ── Enregistrement des factories d'effets ─────────────────────────────────

  registerEffect(id: string, factory: AudioEffectFactory): void {
    this.effectFactories.set(id, factory);
  }

  // ── Connexion d'entrées ───────────────────────────────────────────────────

  async connectMicrophone(config: AudioInputConfig, deviceId?: string): Promise<AudioInput> {
    const ctx = this.getContext();
    await this.resume();

    const constraints: MediaStreamConstraints = {
      audio: deviceId
        ? { deviceId: { exact: deviceId }, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
        : { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: false,
    };

    console.log(`[AudioMixerPipeline] connectMicrophone(${config.id}) — deviceId=${deviceId ?? 'default'}`);
    const stream     = await navigator.mediaDevices.getUserMedia(constraints);
    const sourceNode = ctx.createMediaStreamSource(stream);
    const input      = this.buildInput(config, sourceNode);
    input.ownedStream = stream;
    this.connectToGraph(input);
    return input;
  }

  connectStream(config: AudioInputConfig, stream: MediaStream, owned: boolean = false): AudioInput {

    console.log(`[connectStream] APPELÉ pour ${config.id}`, new Error().stack?.split('\n').slice(1,4).join(' | '));

    const ctx = this.getContext();
    const audioTracks = stream.getAudioTracks();

    console.log(`[AudioMixerPipeline] connectStream(${config.id}) — ${audioTracks.length} piste(s) audio, owned=${owned}`);

    if (!audioTracks.length) {
      console.warn(`[AudioMixerPipeline] connectStream(${config.id}) — AUCUNE piste audio, entrée sans source`);
      const input = this.buildInput(config, null);
      this.connectToGraph(input);
      return input;
    }

    let sourceNode: AudioNode;
    let registryHandle: { release: () => void } | null = null;

    if (this.sourceRegistry) {
      const handle = this.sourceRegistry.borrowFromStream(config.id, stream, 'stream', false);
      sourceNode = handle.sourceNode;
      registryHandle = handle;
      console.log(`[AudioMixerPipeline] connectStream(${config.id}) — sourceNode via registry`);
    } else {
      console.warn(`[AudioMixerPipeline] connectStream(${config.id}) — PAS de registry, fallback createMediaStreamSource`);
      sourceNode = ctx.createMediaStreamSource(stream);
    }

    const input = this.buildInput(config, sourceNode);
    if (owned) input.ownedStream = stream;
    (input as any)._registryHandle = registryHandle;
    this.connectToGraph(input);
    return input;
  }

  connectMediaElement(config: AudioInputConfig, element: HTMLVideoElement | HTMLAudioElement): AudioInput {
    const ctx = this.getContext();
    let sourceNode: MediaElementAudioSourceNode;
    try {
      sourceNode = ctx.createMediaElementSource(element);
      console.log(`[AudioMixerPipeline] connectMediaElement(${config.id}) — sourceNode créé`);
    } catch (e) {
      console.warn(`[AudioMixerPipeline] connectMediaElement(${config.id}) — ÉCHEC:`, e);
      const input = this.buildInput(config, null);
      this.connectToGraph(input);
      return input;
    }

    const input = this.buildInput(config, sourceNode);
    this.connectToGraph(input);
    return input;
  }

  async connectBuffer(config: AudioInputConfig, buffer: AudioBuffer): Promise<AudioInput> {
    const ctx = this.getContext();
    await this.resume();

    const sourceNode    = ctx.createBufferSource();
    sourceNode.buffer   = buffer;
    sourceNode.loop     = config.loop ?? false;

    const start = Math.max(0, config.startTime ?? 0);
    const end   = config.endTime !== undefined ? Math.max(start, config.endTime) : undefined;

    if (sourceNode.loop) {
      // loopStart/loopEnd pilotent la région bouclée ; le 3e argument de start()
      // n'a d'effet que sur la toute première itération, pas sur les suivantes.
      sourceNode.loopStart = start;
      sourceNode.loopEnd   = end ?? 0; // 0 = fin du buffer (comportement par défaut du spec)
    }

    const input         = this.buildInput(config, sourceNode);
    input.bufferSource  = sourceNode;

    sourceNode.onended = () => {
      if (!sourceNode.loop) {
        this.disconnectInput(config.id);
      }
    };

    this.connectToGraph(input);
    if (!sourceNode.loop && end !== undefined) {
      sourceNode.start(0, start, end - start);
    } else {
      sourceNode.start(0, start);
    }
    console.log(`[AudioMixerPipeline] connectBuffer(${config.id}) — buffer démarré, loop=${config.loop}, start=${start}, end=${end ?? 'fin'}`);
    return input;
  }

  // ── Déconnexion ───────────────────────────────────────────────────────────

  disconnectInput(inputId: string): void {
    const input = this.inputs.get(inputId);
    if (!input) return;

    console.log(`[AudioMixerPipeline] disconnectInput(${inputId})`);

    if (input.bufferSource) {
      try { input.bufferSource.stop(); } catch {}
      try { input.bufferSource.disconnect(); } catch {}
      input.bufferSource = null;
    }

    try { input.sourceNode?.disconnect(); } catch {}
    input.effectsChain.forEach(fx => {
      try { fx.destroy(); } catch {}
    });
    try { input.gainNode.disconnect(); } catch {}
    try { input.splitter.disconnect(); } catch {}
    try { input.gainLeft.disconnect(); } catch {}
    try { input.gainRight.disconnect(); } catch {}
    try { input.merger.disconnect(); } catch {}
    try { input.outputGain.disconnect(); } catch {}
    try { input.analyserL.disconnect(); } catch {}
    try { input.analyserR.disconnect(); } catch {}

    if (input.ownedStream) {
      input.ownedStream.getTracks().forEach(t => t.stop());
      input.ownedStream = null;
    }

    const registryHandle = (input as any)._registryHandle as { release: () => void } | undefined;
    if (registryHandle) {
      try { registryHandle.release(); } catch {}
    }

    this.inputs.delete(inputId);
    this.meterCallbacks.delete(inputId);
    this.emit({ type: 'input-disconnected', inputId });
  }

  // ── Méthodes registry output (WHIP → busDest) ─────────────────────────────

  connectRegistryOutput(id: string, mergerNode: AudioNode): void {
    console.log(`[connectRegistryOutput] ${id} — registryOutputs:`, Array.from(this.registryOutputs.keys()));
    const dest = this.busDest;
    if (!dest) return;
    if (this.registryOutputs.has(id)) {
      console.log(`[AudioMixerPipeline] connectRegistryOutput(${id}) — déjà connecté, skip`);
      return;
    }
    console.log(`[connectRegistryOutput] CONNECT: ${id} — registryOutputs actuel:`, Array.from(this.registryOutputs.keys()));
    mergerNode.connect(dest);
    this.registryOutputs.set(id, mergerNode);
    console.log(`[AudioMixerPipeline] connectRegistryOutput(${id}) — connecté`);
  }

  disconnectRegistryOutput(id: string, mergerNode: AudioNode): void {
    const dest = this.busDest;
    if (!dest) return;
    console.log(`[disconnectRegistryOutput] DISCONNECT: ${id}`);
    try { mergerNode.disconnect(dest); } catch {}
    this.registryOutputs.delete(id);
    console.log(`[AudioMixerPipeline] disconnectRegistryOutput(${id}) — déconnecté`);
  }

  // ── Méthodes monitor output (haut-parleurs → ctx.destination) ─────────────
  // v3.3 — Symétrique à connectRegistryOutput / disconnectRegistryOutput.

  connectMonitorOutput(id: string, monitorNode: AudioNode): void {
    if (!this.ctx) return;
    if (this.monitorOutputs.has(id)) {
      console.log(`[AudioMixerPipeline] connectMonitorOutput(${id}) — déjà connecté, skip`);
      return;
    }
    monitorNode.connect(this.ctx.destination);
    this.monitorOutputs.set(id, monitorNode);
    console.log(`[AudioMixerPipeline] connectMonitorOutput(${id}) — connecté`);
  }

  // On NE déconnecte PAS le nœud de ctx.destination : `disconnect(destination)`
  // supprime *toutes* les liaisons entre les deux nœuds, y compris celle posée à la
  // création de la source (AudioSourceRegistry.borrowFromStream) qui garde le graphe
  // vivant. Après une telle déconnexion, tous les appels ultérieurs à setMonitorGain(id, 1)
  // remontaient le gain d'un nœud qui n'atteignait plus la sortie : VU-mètres actifs,
  // fader ouvert, mais aucun son — et seul initAudioMixer pouvait rétablir la liaison.
  // On rend donc la source inaudible par le gain (0.0001 ≈ -80 dB, comme au repos), en
  // conservant le chemin ; l'audibilité reste pilotée par setMonitorGain côté appelant.
  disconnectMonitorOutput(id: string, monitorNode: AudioNode): void {
    if (!this.ctx) return;
    const asGain = monitorNode as GainNode;
    if (asGain.gain) {
      asGain.gain.setTargetAtTime(0.0001, this.ctx.currentTime, 0.02);
    } else {
      try { monitorNode.disconnect(this.ctx.destination); } catch {}
    }
    this.monitorOutputs.delete(id);
    console.log(`[AudioMixerPipeline] disconnectMonitorOutput(${id}) — retiré du monitoring`);
  }

  // ── Contrôle par entrée ───────────────────────────────────────────────────

  setVolume(inputId: string, volume: number): void {
    const input = this.inputs.get(inputId);
    if (!input) return;
    input.volume = volume;
    if (!input.muted) {
      input.gainNode.gain.value = volume;
    }
  }

  setMuted(inputId: string, muted: boolean): void {
    const input = this.inputs.get(inputId);
    if (!input) return;
    input.muted = muted;
    input.gainNode.gain.value = muted ? 0 : input.volume;
  }

  setOutputGain(inputId: string, gain: number): void {
    const input = this.inputs.get(inputId);
    if (!input) return;
    input.outputGain.gain.setTargetAtTime(gain, this.ctx!.currentTime, 0.02);
    console.log(`[AudioMixerPipeline] setOutputGain(${inputId}) → ${gain}`);
  }

  setStereoVolume(inputId: string, left: number, right: number): void {
    const input = this.inputs.get(inputId);
    if (!input) return;
    input.volumeLeft  = left;
    input.volumeRight = right;
    input.gainLeft.gain.value  = left;
    input.gainRight.gain.value = right;
  }

  /**
   * Coupe / rétablit les haut-parleurs pour toutes les entrées directes du
   * mixeur. N'affecte pas le bus de sortie : ce qui part vers MediaMTX reste
   * inchangé. Ne touche pas non plus aux sorties de monitoring du registry
   * (connectMonitorOutput), qui ont leur propre gain.
   */
  setLocalMonitorMuted(muted: boolean): void {
    this.localMonitorMuted = muted;
    if (this.ctx && this.localMonitorGain) {
      this.localMonitorGain.gain.setTargetAtTime(muted ? 0 : 1, this.ctx.currentTime, 0.02);
    }
  }

  setMonitor(inputId: string, monitor: boolean): void {
    const input = this.inputs.get(inputId);
    if (!input) return;
    input.monitor = monitor;
    this.reconnectOutput(input);
  }

  // ── Effets audio par entrée ───────────────────────────────────────────────

  addEffect(inputId: string, effectId: string): AudioEffect | null {
    const input   = this.inputs.get(inputId);
    const factory = this.effectFactories.get(effectId);
    if (!input || !factory) return null;
    const ctx    = this.getContext();
    const effect = factory(ctx);
    input.effectsChain.push(effect);
    this.rewireEffectsChain(input);
    return effect;
  }

  removeEffect(inputId: string, effectId: string): void {
    const input = this.inputs.get(inputId);
    if (!input) return;
    const idx = input.effectsChain.findIndex(fx => fx.id === effectId);
    if (idx === -1) return;
    const effect = input.effectsChain[idx];
    effect.destroy();
    input.effectsChain.splice(idx, 1);
    this.rewireEffectsChain(input);
  }

  toggleEffect(inputId: string, effectId: string, enabled?: boolean): void {
    const input = this.inputs.get(inputId);
    if (!input) return;
    const effect = input.effectsChain.find(fx => fx.id === effectId);
    if (!effect) return;
    effect.setEnabled(enabled ?? !effect.enabled);
    this.rewireEffectsChain(input);
  }

  // ── Mesure de niveaux (metering) ──────────────────────────────────────────

  getLevel(inputId: string): AudioLevel {
    return this.inputs.get(inputId)?.level ?? SILENT_LEVEL;
  }

  onLevel(inputId: string, callback: (level: AudioLevel) => void): void {
    this.meterCallbacks.set(inputId, callback);
    this.ensureMeterLoop();
  }

  offLevel(inputId: string): void {
    this.meterCallbacks.delete(inputId);
    if (this.meterCallbacks.size === 0) {
      this.stopMeterLoop();
    }
  }

  // ── Lecture de l'état ─────────────────────────────────────────────────────

  getInput(inputId: string): AudioInput | undefined {
    return this.inputs.get(inputId);
  }

  getAllInputs(): AudioInput[] {
    return Array.from(this.inputs.values());
  }

  isConnected(inputId: string): boolean {
    return this.inputs.has(inputId);
  }

  // ── Événements ────────────────────────────────────────────────────────────

  subscribe(listener: AudioMixerEventListener): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  private emit(event: AudioMixerEvent): void {
    this.listeners.forEach(fn => {
      try { fn(event); } catch (e) { console.error('[AudioMixerPipeline] Listener error:', e); }
    });
  }

  // ── Préchargement de buffers audio ────────────────────────────────────────

  async decodeAudioUrl(url: string): Promise<AudioBuffer> {
    const ctx = this.getContext();
    let arrayBuf: ArrayBuffer;

    if (url.startsWith('data:')) {
      const base64 = url.split(',')[1];
      const binary = atob(base64);
      arrayBuf     = new ArrayBuffer(binary.length);
      const view   = new Uint8Array(arrayBuf);
      for (let i = 0; i < binary.length; i++) view[i] = binary.charCodeAt(i);
    } else {
      const res = await fetch(url);
      arrayBuf  = await res.arrayBuffer();
    }

    return ctx.decodeAudioData(arrayBuf);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Méthodes internes
  // ══════════════════════════════════════════════════════════════════════════

  private buildInput(config: AudioInputConfig, sourceNode: AudioNode | null): AudioInput {
    const ctx = this.getContext();

    const gainNode  = ctx.createGain();
    gainNode.gain.value = (config.muted ?? false) ? 0 : (config.volume ?? 1);

    const splitter = ctx.createChannelSplitter(2);
    const merger   = ctx.createChannelMerger(2);
    const gainLeft  = ctx.createGain();
    const gainRight = ctx.createGain();
    gainLeft.gain.value  = config.volumeLeft  ?? 1;
    gainRight.gain.value = config.volumeRight ?? 1;

    const analyserL = createAnalyser(ctx);
    const analyserR = createAnalyser(ctx);

    const outputGain = ctx.createGain();
    outputGain.gain.value = 1;

    const input: AudioInput = {
      id:           config.id,
      type:         config.type,
      sourceNode,
      effectsChain: [],
      gainNode,
      gainLeft,
      gainRight,
      splitter,
      merger,
      outputGain,
      analyserL,
      analyserR,
      dataL:        new Uint8Array(analyserL.frequencyBinCount),
      dataR:        new Uint8Array(analyserR.frequencyBinCount),
      level:        { left: 0, right: 0 },
      volume:       config.volume ?? 1,
      muted:        config.muted ?? false,
      volumeLeft:   config.volumeLeft  ?? 1,
      volumeRight:  config.volumeRight ?? 1,
      monitor:      config.monitor ?? true,
      bufferSource: null,
      ownedStream:  null,
    };

    this.inputs.set(config.id, input);
    return input;
  }

  private connectToGraph(input: AudioInput): void {
    const ctx  = this.getContext();
    const dest = this.busDest;
    if (!dest) return;

    if (input.sourceNode) {
      if (input.effectsChain.length > 0) {
        const enabledEffects = input.effectsChain.filter(fx => fx.enabled);
        if (enabledEffects.length > 0) {
          input.sourceNode.connect(enabledEffects[0].inputNode);
          for (let i = 0; i < enabledEffects.length - 1; i++) {
            enabledEffects[i].outputNode.connect(enabledEffects[i + 1].inputNode);
          }
          enabledEffects[enabledEffects.length - 1].outputNode.connect(input.gainNode);
        } else {
          input.sourceNode.connect(input.gainNode);
        }
      } else {
        input.sourceNode.connect(input.gainNode);
      }
    }

    input.gainNode.connect(input.splitter);

    try {
      input.splitter.connect(input.gainLeft, 0);
      input.splitter.connect(input.gainRight, 1);
    } catch {
      input.splitter.connect(input.gainLeft, 0);
      input.splitter.connect(input.gainRight, 0);
    }

    input.gainLeft.connect(input.analyserL);
    input.gainRight.connect(input.analyserR);
    input.analyserL.connect(input.merger, 0, 0);
    input.analyserR.connect(input.merger, 0, 1);

    input.merger.connect(input.outputGain);
    input.outputGain.connect(dest);

    if (input.monitor) {
      input.outputGain.connect(this.localMonitorGain ?? ctx.destination);
    }

    this.emit({ type: 'input-connected', inputId: input.id });
  }

  private reconnectOutput(input: AudioInput): void {
    const ctx  = this.getContext();
    const dest = this.busDest;
    if (!dest) return;

    try { input.outputGain.disconnect(); } catch {}

    input.outputGain.connect(dest);
    if (input.monitor) {
      input.outputGain.connect(this.localMonitorGain ?? ctx.destination);
    }
  }

  private rewireEffectsChain(input: AudioInput): void {
    if (!input.sourceNode) return;

    try { input.sourceNode.disconnect(); } catch {}
    input.effectsChain.forEach(fx => {
      try { fx.inputNode.disconnect(); } catch {}
      try { fx.outputNode.disconnect(); } catch {}
    });

    const enabled = input.effectsChain.filter(fx => fx.enabled);

    if (enabled.length === 0) {
      input.sourceNode.connect(input.gainNode);
    } else {
      input.sourceNode.connect(enabled[0].inputNode);
      for (let i = 0; i < enabled.length - 1; i++) {
        enabled[i].outputNode.connect(enabled[i + 1].inputNode);
      }
      enabled[enabled.length - 1].outputNode.connect(input.gainNode);
    }
  }

  // ── Boucle de mesure ──────────────────────────────────────────────────────

  private ensureMeterLoop(): void {
    if (this.meterRafId !== null) return;
    const loop = () => {
      this.inputs.forEach((input) => {
        input.analyserL.getByteFrequencyData(input.dataL);
        input.analyserR.getByteFrequencyData(input.dataR);
        input.level = {
          left:  peakLevel(input.dataL),
          right: peakLevel(input.dataR),
        };
        const cb = this.meterCallbacks.get(input.id);
        if (cb) cb(input.level);
      });
      this.meterRafId = requestAnimationFrame(loop);
    };
    this.meterRafId = requestAnimationFrame(loop);
  }

  private stopMeterLoop(): void {
    if (this.meterRafId !== null) {
      cancelAnimationFrame(this.meterRafId);
      this.meterRafId = null;
    }
  }
}